import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../authentication/auth.service';

@Injectable()
export class SessionExpiredInterceptor implements HttpInterceptor {
  private processingLogout = false;

  constructor(private authService: AuthService, private router: Router) {}

  private normalizeText(value: any): string {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  private responseHasInvalidTokenMessage(body: any): boolean {
    if (body == null) return false;

    const asText = this.normalizeText(
      typeof body === 'string' ? body : JSON.stringify(body)
    );

    return (
      asText.includes('token es invalido') ||
      asText.includes('token invalido') ||
      asText.includes('iniciar sesion')
    );
  }

  private shouldLogoutByInvalidTokenPayload(error: HttpErrorResponse): boolean {
    const raw = error?.error;
    if (!raw) return false;

    if (typeof raw === 'string' && this.responseHasInvalidTokenMessage(raw)) {
      return true;
    }

    const textPayload =
      typeof raw === 'object' && raw !== null && 'text' in raw
        ? (raw as any).text
        : null;

    if (textPayload && this.responseHasInvalidTokenMessage(textPayload)) {
      return true;
    }

    return this.responseHasInvalidTokenMessage(raw);
  }

  private forceLogoutIfNeeded() {
    if (this.processingLogout || this.router.url.startsWith('/login')) return;
    this.processingLogout = true;
    this.authService.logout();
    queueMicrotask(() => {
      this.processingLogout = false;
    });
  }

  private isProtectedApiRequest(req: HttpRequest<any>): boolean {
    return (
      req.url.includes('/api/v1') &&
      !req.url.includes('/api/v1/users/login')
    );
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          if (this.responseHasInvalidTokenMessage(event.body)) {
            this.forceLogoutIfNeeded();
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (error?.status === 401 && this.isProtectedApiRequest(req)) {
          this.forceLogoutIfNeeded();
        } else if (this.shouldLogoutByInvalidTokenPayload(error)) {
          this.forceLogoutIfNeeded();
        }
        return throwError(() => error);
      })
    );
  }
}
